/**
 * Hand-placement data layer: the per-node position overrides the user
 * drags into place, their localStorage persistence, and an undo/redo
 * history stack.
 *
 * This hook owns only the *data* — the placement map and its history. The
 * drag mechanics that ride alongside a placement (the settle-tail ticker,
 * the `isDragging` flag, the layout warm-start cache) stay with the board
 * layout, because they belong to the solver, not the saved overrides. The
 * board wraps `place`/`reset` with those side effects.
 *
 * History bookkeeping: the first mutation of a drag snapshots the
 * pre-change map into `beforeRef`; a debounced (400ms) commit then pushes
 * that snapshot onto the undo stack, so a continuous drag collapses into a
 * single undo step instead of one per pointer-move.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { PlacementOverrides } from "../board";
import { hostStorage } from "../host/hostStorage.js";

const PLACEMENTS_KEY = "archlang.placements.v1";
const MAX_HISTORY = 100;
const COMMIT_DEBOUNCE_MS = 400;

export function loadPlacements(): PlacementOverrides {
  try {
    const raw = hostStorage.getItem(PLACEMENTS_KEY);
    if (!raw) return new Map();
    const entries = JSON.parse(raw) as Array<
      [string, { x: number; y: number; w: number; h: number }]
    >;
    return new Map(entries);
  } catch {
    return new Map();
  }
}

export function savePlacements(placements: PlacementOverrides): void {
  try {
    if (placements.size === 0) hostStorage.removeItem(PLACEMENTS_KEY);
    else hostStorage.setItem(PLACEMENTS_KEY, JSON.stringify([...placements]));
  } catch {
    /* quota / private mode — silently ignore */
  }
}

export interface PlacementsController {
  /** Current overrides (id → bounds). Drives layout + the reset button. */
  readonly placements: PlacementOverrides;
  /** Ids that currently have an override — i.e. are pinned. */
  readonly pinnedIdSet: ReadonlySet<string>;
  /** Pin/move one node; snapshots history and schedules a commit. */
  readonly place: (id: string, bounds: { x: number; y: number; w: number; h: number }) => void;
  /** Release one node's override; snapshots history and schedules a commit. */
  readonly unpin: (id: string) => void;
  /** Clear every override, pushing the prior map onto the undo stack. */
  readonly reset: () => void;
  readonly undo: () => void;
  readonly redo: () => void;
}

export function usePlacements(): PlacementsController {
  const [placements, setPlacements] = useState<PlacementOverrides>(loadPlacements);

  // Persist across reloads.
  useEffect(() => {
    savePlacements(placements);
  }, [placements]);

  const historyRef = useRef<{ past: PlacementOverrides[]; future: PlacementOverrides[] }>({
    past: [],
    future: [],
  });
  const commitTimerRef = useRef<number | null>(null);
  const beforeRef = useRef<PlacementOverrides | null>(null);

  const commitHistory = useCallback(() => {
    if (beforeRef.current === null) return;
    historyRef.current.past.push(beforeRef.current);
    if (historyRef.current.past.length > MAX_HISTORY) historyRef.current.past.shift();
    historyRef.current.future = [];
    beforeRef.current = null;
  }, []);

  const scheduleCommit = useCallback(() => {
    if (commitTimerRef.current !== null) window.clearTimeout(commitTimerRef.current);
    commitTimerRef.current = window.setTimeout(() => {
      commitHistory();
      commitTimerRef.current = null;
    }, COMMIT_DEBOUNCE_MS);
  }, [commitHistory]);

  const place = useCallback(
    (id: string, bounds: { x: number; y: number; w: number; h: number }) => {
      setPlacements((prev) => {
        if (beforeRef.current === null) beforeRef.current = prev;
        const next = new Map(prev);
        next.set(id, bounds);
        return next;
      });
      scheduleCommit();
    },
    [scheduleCommit],
  );

  const unpin = useCallback(
    (id: string) => {
      setPlacements((prev) => {
        if (!prev.has(id)) return prev;
        if (beforeRef.current === null) beforeRef.current = prev;
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
      scheduleCommit();
    },
    [scheduleCommit],
  );

  const reset = useCallback(() => {
    setPlacements((prev) => {
      historyRef.current.past.push(prev);
      if (historyRef.current.past.length > MAX_HISTORY) historyRef.current.past.shift();
      historyRef.current.future = [];
      return new Map();
    });
  }, []);

  const undo = useCallback(() => {
    // Flush any pending debounced commit first, so the in-progress drag is
    // recorded before we step back past it.
    if (commitTimerRef.current !== null) {
      window.clearTimeout(commitTimerRef.current);
      commitTimerRef.current = null;
      commitHistory();
    }
    setPlacements((current) => {
      const previous = historyRef.current.past.pop();
      if (previous === undefined) return current;
      historyRef.current.future.push(current);
      return previous;
    });
  }, [commitHistory]);

  const redo = useCallback(() => {
    setPlacements((current) => {
      const next = historyRef.current.future.pop();
      if (next === undefined) return current;
      historyRef.current.past.push(current);
      return next;
    });
  }, []);

  const pinnedIdSet = useMemo(() => new Set(placements.keys()), [placements]);

  return { placements, pinnedIdSet, place, unpin, reset, undo, redo };
}
